import { useSettings } from '@backpackjs/storefront';
import { useState, useEffect, useMemo, useRef } from 'react';
import { AddToCart } from '../AddToCart';
import { Body, Headline } from '../Typography';
import { Image } from '../Image';
import { Markdown } from '../Markdown';
import { Svg } from '../Svg';
import { useProductsByHandles } from '../../hooks/useProductsByHandles';

export default function FrequencyCalculator({
  linkedProductHandles,
  product: pdpProduct,
  sectionSettings,
  selectedSellingPlan: pdpSellingPlan,
  selectedVariant: pdpSelectedVariant,
  setSelectedSellingPlanByID: setPdpSelectedSellingPlanByID,
  subscriptionProducts,
}) {
  const settings = useSettings();
  const globalCalculatorSettings = settings?.filterReplacementCalculator;
  const recommendations = globalCalculatorSettings.recommendations.reduce(
    (output, item) => {
      const { handle } = item.product.data;
      output[handle] = item.recommendation;
      return output;
    },
    {}
  );
  const { title, label, ctaWithinCalculator, disclaimer } =
    globalCalculatorSettings;

  const [people, setPeople] = useState(1);
  const linkedProducts = useProductsByHandles({
    handles: linkedProductHandles,
  });

  // This is needed to update the progress portion of the range on webkit ranges
  // Used to update the property vars based on input
  useEffect(() => {
    const range = document.getElementById('people-range');
    if (!range) return undefined;
    const set = () => {
      const min = +range.min || 0;
      const max = +range.max || 100;
      const value = +range.value;
      const percent = ((value - min) * 100) / (max - min);
      range.style.setProperty('--min', String(min));
      range.style.setProperty('--max', String(max));
      range.style.setProperty('--value', String(value));
      range.style.setProperty('--percent', `${percent}%`);
    };
    set();
    range.addEventListener('input', set);
    range.addEventListener('change', set);
    return () => {
      range.removeEventListener('input', set);
      range.removeEventListener('change', set);
    };
  }, []);

  const normalizePlan = (plan) =>
    plan?.sellingPlan ? plan : plan ? { sellingPlan: plan } : null;

  const [selectedPlan, setSelectedPlan] = useState(
    normalizePlan(pdpSellingPlan)
  );
  const [selectedVariant, setSelectedVariant] = useState(pdpSelectedVariant);
  const userPickedRef = useRef(false);

  const products = useMemo(
    () =>
      linkedProducts?.products?.length ? linkedProducts.products : [pdpProduct],
    [linkedProducts?.products, pdpProduct]
  );

  const subscriptions = useMemo(
    () =>
      subscriptionProducts.filter((subscription) =>
        products
          .map((product) => product.handle)
          .includes(subscription.product.handle)
      ),
    [subscriptionProducts, products]
  );

  const getSubscriptionByHandle = (handle) =>
    subscriptions.find(
      (subscription) => subscription.product.handle === handle
    );

  // Recommendation based on the product, and number of people from state
  const recommendedPlans = useMemo(() => {
    const getRecommendedFrequency = (product) =>
      recommendations[product.handle]?.find((rec) => rec.people === people)
        ?.frequency;

    return products.flatMap((product) => ({
      ...product,
      recommendedPlan: product.sellingPlanGroups.find((plan) => {
        const freq = parseInt(
          plan.options
            .find((option) => option.name === 'Order Frequency and Unit')
            // example value: "12-month"
            .values[0].split('-month')
        );
        return freq === getRecommendedFrequency(product);
      }),
    }));
  }, [products, people, recommendations]);

  // Updates product selection when slider or selected product changes for a2c
  useEffect(() => {
    userPickedRef.current = false;
    const currentHandle = selectedVariant?.product?.handle || pdpProduct.handle;
    const target = recommendedPlans.find(
      (product) =>
        product.handle === currentHandle &&
        product.recommendedPlan?.sellingPlans?.[0]
    );
    if (!target) return;
    const nextPlan = { sellingPlan: target.recommendedPlan.sellingPlans[0] };
    if (nextPlan.sellingPlan.id !== selectedPlan?.sellingPlan?.id) {
      setSelectedPlan(nextPlan);
    }
  }, [people]);

  const onChange = (product, sellingPlan) => {
    userPickedRef.current = true;
    setSelectedPlan(sellingPlan);

    // Not switching to a linked product
    if (product.id === pdpProduct.id) {
      setSelectedVariant(pdpSelectedVariant);
      setPdpSelectedSellingPlanByID(sellingPlan.sellingPlan.id);
      return;
    }

    // Linked product only has one variant
    if (product.variants.length === 1) {
      setSelectedVariant(product.variants[0]);
      return;
    }

    // Linked product has multiple variants
    // Try to match based on the PDP variant
    const { selectedOptionsMap: pdpVariantOptions } = pdpSelectedVariant;
    const nextVariant = product.variants.find(
      (variant) => variant.selectedOptionsMap === pdpVariantOptions
    );
    setSelectedVariant(nextVariant || product.variants[0]);
  };

  const getMarkerClassname = (number) => {
    const className = 'range-input__marker absolute -bottom-10';
    if (number === people) {
      return `${className} text-lg range-input__marker--active`;
    }
    return `${className} text-sm opacity-[0.4]`;
  };

  return (
    <div className="frequency-calculator-modal flex h-full grow flex-col gap-4">
      {title && (
        <Headline
          variant="h4"
          element="h4"
          className="mb-4 flex items-center justify-center border-b border-[#DDE2E7] pb-6 text-center"
        >
          <div className="frequency-calculator-modal-title w-">{title}</div>
        </Headline>
      )}

      <div className="grid gap-4 bg-white p-4 text-center md:p-10">
        <div
          className="text-[1.125rem] leading-6 md:leading-[1.125rem] lg:text-xl"
          aria-live="polite"
          aria-atomic="true"
        >
          {people} {people === 1 ? 'Person' : 'People'} = {people}+ fills per
          day (Estimated)
        </div>
        <div className="relative mb-8">
          <input
            className="w-full"
            id="people-range"
            max="4"
            min="1"
            onChange={(e) => setPeople(parseInt(e.target.value))}
            type="range"
            value={people}
          />
          <span className={`${getMarkerClassname(1)} left-[3%] md:left-[1.5%]`}>
            1
          </span>
          <span
            className={`${getMarkerClassname(2)} left-[34%] -translate-x-1/2`}
          >
            2
          </span>
          <span
            className={`${getMarkerClassname(3)} left-[66%] -translate-x-1/2`}
          >
            3
          </span>
          <span
            className={`${getMarkerClassname(
              4
            )} end-0 left-[95%] -translate-x-1/2 md:left-[98%]`}
          >
            4
          </span>
        </div>
        <label
          className="text-[0.65rem] text-xs font-light md:text-sm"
          htmlFor="people-range"
        >
          {sectionSettings.labelOverride || label}
        </label>
      </div>

      <section className="grow">
        <Headline
          variant="h5"
          element="h5"
          className="mb-4 mt-4 text-base md:mb-8 md:mt-8"
        >
          Your recommended filter plan
        </Headline>
        <div className="leading-8">
          {recommendedPlans.map((product) => {
            if (!product?.recommendedPlan) return;
            const { shipsRecurringly } =
              getSubscriptionByHandle(product.handle) || {};
            if (!shipsRecurringly) return;
            const { image, items } = shipsRecurringly;
            // There's always one sellingPlan per plan
            const sellingPlan = product.recommendedPlan.sellingPlans[0];
            const { id, name: planName } = sellingPlan;
            const isSelected = id === selectedPlan?.sellingPlan?.id;

            return (
              <div
                className="flex flex-row items-center justify-between gap-4 border-t border-[#DDE2E7] py-2"
                key={id}
              >
                <div className="flex flex-row items-center gap-4 text-xs font-light leading-[1.25rem] md:text-base">
                  <Image
                    alt=""
                    className="max-w-[50px]"
                    // key to prevent the image from re-rendering when switching plans
                    key={product.handle}
                    src={image.src}
                    width="50"
                    height="50"
                  />
                  {items
                    ?.replace(/^\*/, '') // remove bullet point
                    .replace(/\s*\([^)]*%\s*Off\)/gi, '') // remove (X% Off)
                    .replaceAll('{qty}x', '')}{' '}
                  - {planName}
                </div>

                <div>
                  <input
                    checked={isSelected}
                    className="sr-only"
                    id={`radio-${id}`}
                    name="selected-plan"
                    onChange={() => onChange(product, { sellingPlan })}
                    type="radio"
                    value={id}
                  />
                  <label
                    aria-label={`Select ${planName}`}
                    className="cursor-pointer text-sm text-primary underline"
                    htmlFor={`radio-${id}`}
                  >
                    {isSelected ? (
                      <span className="flex flex-row gap-2 text-success underline">
                        Selected!{' '}
                        <Svg
                          className="w-[24px]"
                          src="/svgs/circle-check.svg#circle-check"
                          title="Check"
                          viewBox="0 0 20 20"
                        />
                      </span>
                    ) : (
                      'Select'
                    )}
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        {ctaWithinCalculator && (
          <AddToCart
            addToCartText={ctaWithinCalculator}
            className="mt-[1.2rem] grow md:mt-[2.5rem]"
            selectedVariant={selectedVariant}
            purchaseType="subscription"
            selectedSellingPlan={selectedPlan}
          />
        )}
      </section>
      <footer>
        <Markdown className="pt-[4rem] text-xs font-light md:pt-[0.5rem] [&_p]:mb-2 [&_p]:lg:text-xs [&_p]:lg:leading-[1.4]">
          {sectionSettings.disclaimerOverride || disclaimer}
        </Markdown>
      </footer>
    </div>
  );
}
